import React from 'react';
import { HoverTooltip } from '../HoverTooltip';
import { ArrayFieldTemplateProps, ArrayFieldTemplateItemType } from '@rjsf/utils';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown, Delete } from '../ProjectIcons';
import { buttonClasses } from '../tailwindClassnames';

// Template for rjsf-rendered arrays (arrays of objects — see generateUISchema's
// hasScalarItems gate; scalar arrays go through CustomArrayField instead).
// Lays the title and description full-width above the item list so nested
// arrays keep the whole card width instead of halving it at every level.
const CustomArrayFieldTemplate = (props: ArrayFieldTemplateProps) => {
    const { title, schema, items, canAdd, onAddClick, disabled, readonly, required } = props;
    const { t } = useTranslation();

    return (
        <div className="my-2 w-full">
            {(title || schema.description) && (
                <div className="flex flex-col mb-2">
                    {title && (
                        <label className="font-semibold">
                            {title}
                            {required && <span className="ml-1">*</span>}
                        </label>
                    )}
                    {schema.description && (
                        <span className="text-ui-text-primary-text text-sm mt-1">{schema.description}</span>
                    )}
                </div>
            )}
            {items.map(({
                key,
                index,
                children,
                hasToolbar,
                hasMoveUp,
                hasMoveDown,
                hasRemove,
                onReorderClick,
                onDropIndexClick,
            }: ArrayFieldTemplateItemType) => (
                <div
                    key={key}
                    className="mb-3 border border-unlisted-gray-lines rounded w-full p-3 transition-all duration-300 flex flex-row items-start gap-2"
                    data-testid={`CustomArrayFieldTemplate-item-${index}`}
                >
                    <div className="flex-1 min-w-0">{children}</div>
                    {/* A flex sibling, not an absolute overlay on the fields. pt-3 is
                        where the first input starts (pt-1 + p-2 in
                        HorizontalFieldTemplate), so the buttons line up with it. */}
                    {hasToolbar && !readonly && (
                        <div className="flex flex-row items-center gap-1 shrink-0 pt-3">
                            {(hasMoveUp || hasMoveDown) && (
                                <>
                                    <HoverTooltip content={t('Move up') || 'Move up'} position="top" focusable={false}>
                                        <button
                                            type="button"
                                            className="p-1 rounded hover:bg-greyscale-10 disabled:opacity-30 disabled:cursor-not-allowed"
                                            aria-label={t('Move up') || 'Move up'}
                                            disabled={disabled || !hasMoveUp}
                                            onClick={onReorderClick(index, index - 1)}
                                        >
                                            <ArrowUp />
                                        </button>
                                    </HoverTooltip>
                                    <HoverTooltip content={t('Move down') || 'Move down'} position="top" focusable={false}>
                                        <button
                                            type="button"
                                            className="p-1 rounded hover:bg-greyscale-10 disabled:opacity-30 disabled:cursor-not-allowed"
                                            aria-label={t('Move down') || 'Move down'}
                                            disabled={disabled || !hasMoveDown}
                                            onClick={onReorderClick(index, index + 1)}
                                        >
                                            <ArrowDown />
                                        </button>
                                    </HoverTooltip>
                                </>
                            )}
                            {hasRemove && (
                                <HoverTooltip content={t('Remove') || 'Remove'} position="top" focusable={false}>
                                    <button
                                        type="button"
                                        className="p-1 rounded hover:bg-greyscale-10 disabled:opacity-30 disabled:cursor-not-allowed"
                                        aria-label={t('Remove') || 'Remove'}
                                        disabled={disabled}
                                        onClick={onDropIndexClick(index)}
                                    >
                                        <Delete />
                                    </button>
                                </HoverTooltip>
                            )}
                        </div>
                    )}
                </div>
            ))}
            {canAdd && !readonly && (
                <div className="flex justify-end mt-2">
                    <button
                        type="button"
                        className={buttonClasses.lightGreen}
                        disabled={disabled}
                        onClick={onAddClick}
                        data-testid="CustomArrayFieldTemplate-add-button"
                    >
                        {t('Add Item')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CustomArrayFieldTemplate;
